import 'react-phone-input-2/lib/material.css';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import {
  getContactInformationDropdowns,
  getContactInformationFormData,
  getCorpKycContactInfo,
  getCountriesDropdowns,
  postContactInformation,
  saveCorpKycContactInfo,
} from '../../../store/actions/corporateAction';
import { getContentKeyAccToLang } from '../../../utils/utils';
import { Chips, ErrorText, PhoneInput, RowView, Text } from '../components';
import ClientText from '../../../components/Text';
import { Dropdown } from '../../../components';
import { fontSizes } from '../../../constants/constants';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import dynamicfunction from '../../../utils/dynamicfunction';
import { contact_info_validation_schema } from '../../../validations_schemas/contact_information_schema';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import { integer_regex } from '../../../utils/functions';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const ContactInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isArabic, TextWeight } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { countries_dropdown, corporate_post_loading, contact_information_dropdowns, contact_information_data } = useSelector(
    (state) => state.corporate,
  );
  const { client_application_id, is_kyc, is_ksa_corporate } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_contact_information?.data?.attributes, key, isArabic);
  };

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getContactInformationDropdowns());
    await dispatch(getCountriesDropdowns());
    await dispatch(is_kyc ? getCorpKycContactInfo(client_application_id) : getContactInformationFormData(client_application_id));
  };

  const onSubmit = (values) => {
    const api_data = {
      contactPersonName: values?.contactPersonName,
      jobTitle: values?.jobTitle,
      workPhoneNumber: values?.workPhoneNumber,
      mobileNumber: values?.mobileNumber,
      workEmail: values?.workEmail,
      buildingNumberKsa: values?.buildingNumberKsa ?? null,
      streetNameKsa: values?.streetNameKsa ?? null,
      districtKsa: values?.districtKsa ?? null,
      cityKsa: values?.cityKsa ?? null,
      postalCodeKsa: values?.postalCodeKsa ?? null,
      additionalNumberKsa: values?.additionalNumberKsa ?? null,
      unitNoKsa: values?.unitNoKsa ?? null,
      countryNonKsa: values?.countryNonKsa ?? null,
      preferredLanguageForCorrespondence: values?.preferredLanguageForCorrespondence,
      preferredCorrespondence: values?.contactPreferenceEmail,
      corpClientApplicationId: Number(client_application_id),
      anyOtherFinancialInfo: values?.anyOtherFinancialInfo,
    };

    dispatch(
      is_kyc
        ? saveCorpKycContactInfo(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        })
        : postContactInformation(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? draftValues.current : {
          contactPersonName: contact_information_data?.contactPersonName ?? '',
          jobTitle: contact_information_data?.jobTitle ?? '',
          workPhoneNumber: contact_information_data?.workPhoneNumber ?? '',
          mobileNumber: contact_information_data?.mobileNumber ?? '',
          workEmail: contact_information_data?.workEmail ?? '',
          preferredLanguageForCorrespondence: contact_information_data?.preferredLanguageForCorrespondence ?? '',
          contactPreferenceEmail: contact_information_data?.preferredCorrespondence ?? [],
          countryNonKsa: contact_information_data?.countryNonKsa ?? null,
          buildingNumberKsa: contact_information_data?.buildingNumberKsa ?? '',
          streetNameKsa: contact_information_data?.streetNameKsa ?? '',
          districtKsa: contact_information_data?.districtKsa ?? '',
          cityKsa: contact_information_data?.cityKsa ?? '',
          postalCodeKsa: contact_information_data?.postalCodeKsa ?? '',
          additionalNumberKsa: contact_information_data?.additionalNumberKsa ?? '',
          unitNoKsa: contact_information_data?.unitNoKsa ?? '',
          anyOtherFinancialInfo: contact_information_data?.anyOtherFinancialInfo ?? '',
          isSaveExist: false,
          isKsa: is_ksa_corporate,
        }}
        validationSchema={contact_info_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, setFieldTouched, touched, errors }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='gap-7' isResponsive>
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('name_of_contact')}
                  value={values?.contactPersonName}
                  onChange={(e) => {
                    setFieldValue('contactPersonName', e.target.value);
                    setFieldTouched('contactPersonName', true, false);
                  }}
                  placeholder={getContent('placeholder_name_of_contact')}
                  errorMessage={getContent(touched?.contactPersonName ? errors?.contactPersonName : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('position')}
                  value={values?.jobTitle}
                  onChange={(e) => {
                    setFieldValue('jobTitle', e.target.value);
                    setFieldTouched('jobTitle', true, false);
                  }}
                  placeholder={getContent('placeholder_position')}
                  errorMessage={getContent(touched?.jobTitle ? errors?.jobTitle : '')}
                />
                <div className='w-full flex flex-col gap-1'>
                  <ClientText text={getContent('office_phone_number')} size={fontSizes.h14} />
                  <PhoneInput
                    value={values?.workPhoneNumber}
                    onChange={(val) => {
                      setFieldValue('workPhoneNumber', val);
                      setFieldTouched('workPhoneNumber', true, false);
                    }}
                  />
                  <ErrorText text={getContent(touched?.workPhoneNumber ? errors?.workPhoneNumber : '')} />
                </div>
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <div className='w-full'>
                  <ClientText text={getContent('mobile_number')} size={fontSizes.h14} isRequired={true} className={'mb-1'} />
                  <PhoneInput
                    value={values?.mobileNumber}
                    onChange={(val) => {
                      setFieldValue('mobileNumber', val);
                      setFieldTouched('mobileNumber', true, false);
                    }}
                    isDisabled={!is_kyc}
                  />
                  <ErrorText text={getContent(touched?.mobileNumber ? errors?.mobileNumber : '')} />
                </div>
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('email')}
                  value={values?.workEmail}
                  onChange={(e) => {
                    setFieldValue('workEmail', e.target.value);
                    setFieldTouched('workEmail', true, false);
                  }}
                  placeholder={getContent('placeholder_email')}
                  errorMessage={getContent(touched?.workEmail ? errors?.workEmail : '')}
                />
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <div className='w-full'>
                  <Chips
                    label={getContent('preferred_language')}
                    value={values?.preferredLanguageForCorrespondence}
                    onChange={(e) => {
                      setFieldValue('preferredLanguageForCorrespondence', e);
                      setFieldTouched('preferredLanguageForCorrespondence', false);
                    }}
                    options={contact_information_dropdowns?.preferredLanguageForCorrespondence}
                  />
                  <ErrorText
                    text={getContent(touched?.preferredLanguageForCorrespondence ? errors?.preferredLanguageForCorrespondence : '')}
                    className='block'
                  />
                </div>
                <div className='w-full'>
                  <Chips
                    label={getContent('preferred_correspondence')}
                    value={values?.contactPreferenceEmail}
                    onChange={(e) => {
                      setFieldValue('contactPreferenceEmail', e);
                      setFieldTouched('contactPreferenceEmail', false);
                    }}
                    options={contact_information_dropdowns?.preferredCorrespondence}
                    isMulti
                  />
                  <ErrorText text={getContent(touched?.contactPreferenceEmail ? errors?.contactPreferenceEmail : '')} className='block' />
                </div>
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>

              <Text
                text={getContent('address_heading')}
                size={fontSizes.h22}
                isTruncate={false}
                weight={TextWeight.semiBold}
                className='pt-3 sm:pt-5 block'
              />

              <Text text={getContent('national_address_if_ksa_resident')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country')}
                  placeholder={getContent('placeholder_country')}
                  options={countries_dropdown}
                  selectedValue={countries_dropdown?.find((opt) => opt?.id === values?.countryNonKsa || opt?.name === values?.countryNonKsa)}
                  valueKey={'name'}
                  isRequired={values?.isKsa}
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('countryNonKsa', val?.id ?? val?.name ?? val);
                    setFieldTouched('countryNonKsa', false);
                  }}
                  errorMessage={getContent(touched?.countryNonKsa ? errors?.countryNonKsa : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('building_number')}
                  value={values?.buildingNumberKsa}
                  onChange={(e) => {
                    setFieldValue('buildingNumberKsa', e.target.value);
                    setFieldTouched('buildingNumberKsa', true, false);
                  }}
                  placeholder={getContent('placeholder_building_number')}
                  errorMessage={getContent(touched?.buildingNumberKsa ? errors?.buildingNumberKsa : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('street_name')}
                  value={values?.streetNameKsa}
                  onChange={(e) => {
                    setFieldValue('streetNameKsa', e.target.value);
                    setFieldTouched('streetNameKsa', true, false);
                  }}
                  placeholder={getContent('placeholder_street_name')}
                  errorMessage={getContent(touched?.streetNameKsa ? errors?.streetNameKsa : '')}
                  maxLength={50}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('district')}
                  value={values?.districtKsa}
                  onChange={(e) => {
                    setFieldValue('districtKsa', e.target.value);
                    setFieldTouched('districtKsa', true, false);
                  }}
                  placeholder={getContent('placeholder_district')}
                  errorMessage={getContent(touched?.districtKsa ? errors?.districtKsa : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('city')}
                  value={values?.cityKsa}
                  onChange={(e) => {
                    setFieldValue('cityKsa', e.target.value);
                    setFieldTouched('cityKsa', true, false);
                  }}
                  placeholder={getContent('placeholder_city')}
                  errorMessage={getContent(touched?.cityKsa ? errors?.cityKsa : '')}
                  maxLength={20}
                />
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('zip_code')}
                  value={values?.postalCodeKsa}
                  onChange={(e) => {
                    setFieldValue('postalCodeKsa', e.target.value);
                    setFieldTouched('postalCodeKsa', true, false);
                  }}
                  maxLength={10}
                  placeholder={getContent('placeholder_zip_code')}
                  errorMessage={getContent(touched?.postalCodeKsa ? errors?.postalCodeKsa : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  isRounded
                  label={getContent('additional_number')}
                  value={values?.additionalNumberKsa}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || integer_regex.test(value)) {
                      setFieldValue('additionalNumberKsa', value);
                      setFieldTouched('additionalNumberKsa', true, false);
                    }
                  }}
                  maxLength={15}
                  placeholder={getContent('placeholder_additional_number')}
                  errorMessage={getContent(touched?.additionalNumberKsa ? errors?.additionalNumberKsa : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={values?.isKsa}
                  label={getContent('unit_number')}
                  value={values?.unitNoKsa}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || integer_regex.test(value)) {
                      setFieldValue('unitNoKsa', value);
                      setFieldTouched('unitNoKsa', true, false);
                    }
                  }}
                  placeholder={getContent('placeholder_unit_number')}
                  errorMessage={getContent(touched?.unitNoKsa ? errors?.unitNoKsa : '')}
                />
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>

              <Text
                text={getContent('other_Information_heading')}
                size={fontSizes.h22}
                isTruncate={false}
                weight={TextWeight.semiBold}
                className={'block pt-3 sm:pt-5'}
              />

              <RowView className='gap-7 pt-4'>
                <CustomInput
                  isRounded
                  label={getContent('other_financial_info_client')}
                  value={values?.anyOtherFinancialInfo}
                  onChange={(e) => {
                    setFieldValue('anyOtherFinancialInfo', e.target.value);
                    setFieldTouched('anyOtherFinancialInfo', true, false);
                  }}
                  labelDivClass={"h-14 sm:h-6"}
                  placeholder={getContent('placeholder_other_financial_info_client')}
                  errorMessage={getContent(touched?.anyOtherFinancialInfo ? errors?.anyOtherFinancialInfo : '')}
                />
              </RowView>
              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  loading={corporate_post_loading}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default ContactInformation;
